<script setup>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";

import {
  sessionsWidgetCreateClicked,
  sessionsWidgetDuplicateClicked,
  sessionsWidgetSessionOpened,
  sessionsWidgetViewAllClicked,
} from "@/analytics/events";
import { trackEvent } from "@/analytics/track";
import * as draftStorage from "@/components/session/sessionDraftStorage";
import { SessionsHandler } from "@/handlers/sessionsHandler";
import DuplicateModal from "@/pages/authRequired/DuplicateModal.vue";
import { useAppStore } from "@/stores";
import { createHandlerContext } from "@/stores/context";
import { appBoardUrl } from "@/utils/url";

import BaseButton from "../BaseButton.vue";
import BaseSpinner from "../BaseSpinner.vue";
import SessionPhaseBadge from "../SessionPhaseBadge.vue";
import SvgIcon from "../SvgIcon.vue";
import KebabMenu from "./KebabMenu.vue";
import WidgetCard from "./WidgetCard.vue";

const MAX_VISIBLE = 5;

const router = useRouter();
const store = useAppStore();

const sessions = ref([]);
const isLoading = ref(true);
const loadError = ref(false);
const duplicateModal = ref(null);

async function load() {
  isLoading.value = true;
  loadError.value = false;
  try {
    const data = await new SessionsHandler(createHandlerContext()).getSessions();
    sessions.value = data || [];
  } catch (e) {
    loadError.value = true;
    throw e;
  } finally {
    isLoading.value = false;
  }
}

onMounted(load);

const activeSessions = computed(() => sessions.value.filter((s) => !s.archived));

const sortedSessions = computed(() =>
  [...activeSessions.value].sort((a, b) => (b.creation_date || 0) - (a.creation_date || 0)),
);

const visibleSessions = computed(() => sortedSessions.value.slice(0, MAX_VISIBLE));

function openSession(session) {
  trackEvent(sessionsWidgetSessionOpened());
  router.push({
    name: "editSession",
    query: { path: "generalInformation", id: session.session_id },
  });
}

function openBoard(session) {
  window.open(appBoardUrl(session.session_id, "program"), "_blank", "noopener");
}

function duplicateSession(session) {
  trackEvent(sessionsWidgetDuplicateClicked());

  duplicateModal.value.show(session.session_id);
}

function goAll() {
  trackEvent(sessionsWidgetViewAllClicked());
  router.push({ name: "safepiplannings" });
}

function createSession() {
  trackEvent(sessionsWidgetCreateClicked());
  // Start a clean draft, mirroring SafePiPlanningsPage.addPiPlanning — otherwise
  // a leftover draft (e.g. an abandoned duplicate) leaks into the new session.
  draftStorage.clearDraft();
  store.clearStateManager();
  draftStorage.setSettings({ iteration_load_critical: 100 });
  router.push({ name: "addnewsafepiplanning" });
}
</script>

<template>
  <WidgetCard class="sessions-widget" icon="general/pi" :title-to="{ name: 'safepiplannings' }">
    <template #title>PI sessions</template>
    <template #actions>
      <BaseButton
        variant="outlined"
        color="grey"
        size="small"
        icon-before="system/plus"
        aria-label="New session"
        @click="createSession"
      />
    </template>

    <ul v-if="visibleSessions.length" class="list">
      <li v-for="session in visibleSessions" :key="session.session_id" class="row-item">
        <button type="button" class="row" @click="openSession(session)">
          <span class="name-cell">
            <span class="name">{{ session.name }}</span>
            <SessionPhaseBadge :session />
          </span>
        </button>
        <KebabMenu :label="`Actions for ${session.name}`">
          <!-- eslint-disable vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/interactive-supports-focus -->
          <li tabindex="-1" role="menuitem" class="list-item" @click="openSession(session)">
            Edit session
          </li>
          <li tabindex="-1" role="menuitem" class="list-item" @click="openBoard(session)">
            Open in planning app
          </li>
          <li tabindex="-1" role="menuitem" class="list-item" @click="duplicateSession(session)">
            Duplicate
          </li>
          <!-- eslint-enable vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/interactive-supports-focus -->
        </KebabMenu>
      </li>
    </ul>

    <div v-else-if="isLoading" class="loading">
      <BaseSpinner delayed size="20" />
    </div>

    <div v-else-if="loadError" class="empty">
      <p class="empty-copy">Something went wrong while loading PI sessions.</p>
    </div>

    <div v-else class="empty">
      <p class="empty-copy">No PI sessions yet. They'll show up here once you create one.</p>
    </div>

    <template v-if="activeSessions.length" #footer>
      <button type="button" class="view-all-link" @click="goAll">
        View all sessions
        <SvgIcon name="system/chevron-down" width="14" height="14" class="view-all-chevron" />
      </button>
      <span class="footer-meta"> {{ activeSessions.length }} total </span>
    </template>
  </WidgetCard>
  <DuplicateModal ref="duplicateModal" />
</template>

<style lang="scss" scoped>
@use "@/components-ng/home/widget-list";
@include widget-list.chrome;

.name-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.session-phase-badge {
  flex-shrink: 0;
}
</style>
